import { useEffect, useRef } from 'react';
import type { OpenChart } from './useOpenCharts';

export interface OpenChartTabsProps {
  charts: OpenChart[];
  onOpen: (id: string) => void;
  onClose: (id: string) => void;
  /** 'chip' sits inside the top bar (shell A); 'tab' fills its own strip (shell B). */
  variant: 'chip' | 'tab';
}

/** The open patient charts. Close buttons are always visible (no hover-only UI). */
export default function OpenChartTabs({ charts, onOpen, onClose, variant }: OpenChartTabsProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeId = charts.find((c) => c.active)?.id;

  useEffect(() => {
    if (!activeId || !scrollRef.current) return;
    scrollRef.current.querySelector(`[data-chart-id="${CSS.escape(activeId)}"]`)?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activeId]);

  if (!charts.length) return null;

  return (
    <div
      ref={scrollRef}
      role="tablist"
      aria-label="Open charts"
      className={`flex min-w-0 flex-1 overflow-x-auto [scrollbar-width:none] ${variant === 'chip' ? 'items-center gap-1' : 'items-stretch gap-px'}`}
    >
      {charts.map((c) => {
        const base = variant === 'chip'
          ? `h-[30px] rounded-md border pl-2.5 pr-1 ${c.active
            ? 'border-primary-200 bg-primary-50 font-semibold text-slate-900 dark:border-primary-800 dark:bg-primary-900/40 dark:text-slate-50'
            : 'border-transparent text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800'}`
          : `pl-3.5 pr-1.5 ${c.active
            ? 'bg-white font-semibold text-slate-900 shadow-[inset_0_2px_0_#2563eb,1px_0_0_#e2e8f0,-1px_0_0_#e2e8f0] dark:bg-slate-900 dark:text-slate-50 dark:shadow-[inset_0_2px_0_#60a5fa,1px_0_0_#334155,-1px_0_0_#334155]'
            : 'text-slate-700 hover:bg-white/70 dark:text-slate-300 dark:hover:bg-slate-800'}`;
        return (
          <div key={c.id} data-chart-id={c.id} className={`flex flex-none items-center gap-1 text-[13px] ${base}`}>
            <button
              type="button"
              role="tab"
              aria-selected={c.active}
              onClick={() => onOpen(c.id)}
              className="flex items-center gap-1.5 whitespace-nowrap"
            >
              {c.status && <span aria-hidden="true" className={`h-2 w-2 flex-none rounded-full ${c.status.dot}`} />}
              <span>{c.label}</span>
              {c.meta && <span className="text-xs font-normal text-slate-600 dark:text-slate-400">{c.meta}</span>}
              {c.status && <span className="sr-only">, {c.status.label}</span>}
            </button>
            <button
              type="button"
              onClick={() => onClose(c.id)}
              aria-label={`Close chart ${c.label}`}
              title="Close (⌘W)"
              className="flex h-[22px] w-[22px] items-center justify-center rounded text-slate-500 hover:bg-slate-200 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-50"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
            </button>
          </div>
        );
      })}
    </div>
  );
}
